import { useState, useEffect, FormEvent } from 'react';
import { useParams } from 'react-router-dom';
import { confirmAlert } from 'react-confirm-alert';
import 'react-confirm-alert/src/react-confirm-alert.css';

import Loading from 'components/Loading';
import { IconAddCircle, IconDeleteOutline, IconSave } from 'components/Icons';
import { showToast, dismissToast, TOAST_DEFAULT, TOAST_SUCCESS, TOAST_ERROR, ONE_MINUTE, TEN_SECONDS } from 'lib/utils';
import { useGetEmailWhitelist, useSetEmailWhitelist } from 'hooks/useEmailWhitelist';

import './BookkeeperEmailWhitelist.scss';

// Domain regex: allows alphanumeric, hyphens between parts, must have valid TLD
// e.g., example.com, sub.example.co.uk, my-company.org
const DOMAIN_REGEX = /^(?!-)[A-Za-z0-9-]+(\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}$/;

// Email regex: standard email validation
const EMAIL_REGEX = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;

const isValidDomain = (domain: string): boolean => {
  if (!domain || domain.length > 253) return false;
  // Check for invalid characters
  if (/[^a-zA-Z0-9.-]/.test(domain)) return false;
  // Check for consecutive dots
  if (domain.includes('..')) return false;
  // Check each segment length (max 63 chars)
  const segments = domain.split('.');
  if (segments.some(seg => seg.length === 0 || seg.length > 63)) return false;
  // Check segment doesn't start or end with hyphen
  if (segments.some(seg => seg.startsWith('-') || seg.endsWith('-'))) return false;
  return DOMAIN_REGEX.test(domain);
};

const isValidEmail = (email: string): boolean => {
  if (!email || email.length > 254) return false;
  return EMAIL_REGEX.test(email);
};

function BookkeeperEmailWhitelist(): JSX.Element {
  const { locationId } = useParams();

  const [domains, setDomains] = useState<string[]>([]);
  const [emails, setEmails] = useState<string[]>([]);
  const [newDomain, setNewDomain] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [isDirty, setIsDirty] = useState(false);

  const { data, isLoading, error } = useGetEmailWhitelist(locationId);
  const setWhitelistMutation = useSetEmailWhitelist(locationId);

  useEffect(() => {
    if (data && 'whitelist' in data) {
      setDomains(data.whitelist?.domains || []);
      setEmails(data.whitelist?.emails || []);
    }
  }, [data]);

  const handleSave = () => {
    const toastId = showToast('Saving whitelist...', TOAST_DEFAULT, ONE_MINUTE);

    setWhitelistMutation.mutate(
      { domains, emails },
      {
        onSuccess: () => {
          toastId && dismissToast(toastId);
          showToast('Whitelist saved successfully', TOAST_SUCCESS, TEN_SECONDS);
          setIsDirty(false);
        },
        onError: err => {
          toastId && dismissToast(toastId);
          showToast(`Error saving whitelist: ${err}`, TOAST_ERROR, TEN_SECONDS);
        },
      }
    );
  };

  const handleAddDomain = (e: FormEvent) => {
    e.preventDefault();
    const trimmedDomain = newDomain.trim().toLowerCase();
    if (!trimmedDomain) {
      showToast('Please enter a domain', TOAST_ERROR, TEN_SECONDS);
      return;
    }
    if (!isValidDomain(trimmedDomain)) {
      showToast('Please enter a valid domain (e.g., example.com)', TOAST_ERROR, TEN_SECONDS);
      return;
    }
    if (domains.includes(trimmedDomain)) {
      showToast('Domain already exists', TOAST_ERROR, TEN_SECONDS);
      return;
    }
    setDomains([...domains, trimmedDomain]);
    setNewDomain('');
    setIsDirty(true);
  };

  const handleAddEmail = (e: FormEvent) => {
    e.preventDefault();
    const trimmedEmail = newEmail.trim().toLowerCase();
    if (!trimmedEmail) {
      showToast('Please enter an email', TOAST_ERROR, TEN_SECONDS);
      return;
    }
    if (!isValidEmail(trimmedEmail)) {
      showToast('Please enter a valid email address (e.g., user@example.com)', TOAST_ERROR, TEN_SECONDS);
      return;
    }
    if (emails.includes(trimmedEmail)) {
      showToast('Email already exists', TOAST_ERROR, TEN_SECONDS);
      return;
    }
    setEmails([...emails, trimmedEmail]);
    setNewEmail('');
    setIsDirty(true);
  };

  const handleRemoveDomain = (domain: string) => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <div className="alert-ui">
          <p className="alert-ui__title">Remove this domain?</p>
          <p className="alert-ui__value">{domain}</p>
          <div className="alert-ui__actions">
            <button className="button BKForm-btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              className="button BKForm-btn"
              onClick={() => {
                setDomains(domains.filter(d => d !== domain));
                setIsDirty(true);
                onClose();
              }}
            >
              Remove
            </button>
          </div>
        </div>
      ),
    });
  };

  const handleRemoveEmail = (email: string) => {
    confirmAlert({
      customUI: ({ onClose }) => (
        <div className="alert-ui">
          <p className="alert-ui__title">Remove this email?</p>
          <p className="alert-ui__value">{email}</p>
          <div className="alert-ui__actions">
            <button className="button BKForm-btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button
              className="button BKForm-btn"
              onClick={() => {
                setEmails(emails.filter(e => e !== email));
                setIsDirty(true);
                onClose();
              }}
            >
              Remove
            </button>
          </div>
        </div>
      ),
    });
  };

  if (isLoading) {
    return (
      <div className="bookkeeper-tab-container">
        <div className="bk-whitelist-container">
          <Loading loadingCircleClass="Loading-circle-small" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bookkeeper-tab-container">
        <div className="bk-whitelist-container">
          <div className="error-message">Error loading whitelist: {String(error)}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bookkeeper-tab-container">
      <div className="bk-whitelist-container">
        <div className="bk-whitelist-container__header">
          <div className="bk-whitelist-container__title">Email Whitelist</div>
          <button
            className="button button-primary save-button"
            onClick={handleSave}
            disabled={!isDirty || setWhitelistMutation.isPending}
          >
            <IconSave /> Save Changes
          </button>
        </div>

        <div className="bk-whitelist-container__content">
          {/* Domains Section */}
          <div className="bk-whitelist-section">
            <div className="bk-whitelist-section__header">
              <h3>Allowed Domains</h3>
              <form onSubmit={handleAddDomain} className="add-form">
                <input
                  type="text"
                  placeholder="e.g. example.com"
                  value={newDomain}
                  onChange={e => setNewDomain(e.target.value)}
                />
                <button type="submit" className="button button-primary add-button">
                  <IconAddCircle /> Add
                </button>
              </form>
            </div>
            <div className="bk-whitelist-section__list">
              {domains.length === 0 ? (
                <div className="empty-message">No domains added yet</div>
              ) : (
                domains.map(domain => (
                  <div key={domain} className="list-item">
                    <span className="list-item__value">{domain}</span>
                    <button
                      className="button BKForm-btn-alert remove-button"
                      onClick={() => handleRemoveDomain(domain)}
                    >
                      <IconDeleteOutline />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Emails Section */}
          <div className="bk-whitelist-section">
            <div className="bk-whitelist-section__header">
              <h3>Allowed Emails</h3>
              <form onSubmit={handleAddEmail} className="add-form">
                <input
                  type="email"
                  placeholder="e.g. user@example.com"
                  value={newEmail}
                  onChange={e => setNewEmail(e.target.value)}
                />
                <button type="submit" className="button button-primary add-button">
                  <IconAddCircle /> Add
                </button>
              </form>
            </div>
            <div className="bk-whitelist-section__list">
              {emails.length === 0 ? (
                <div className="empty-message">No emails added yet</div>
              ) : (
                emails.map(email => (
                  <div key={email} className="list-item">
                    <span className="list-item__value">{email}</span>
                    <button className="button BKForm-btn-alert remove-button" onClick={() => handleRemoveEmail(email)}>
                      <IconDeleteOutline />
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {isDirty && <div className="unsaved-changes-notice">You have unsaved changes</div>}
      </div>
    </div>
  );
}

export default BookkeeperEmailWhitelist;
